import { getSerial, SerialPort } from './web-serial.types';
import {
  getPortSettings,
  getSerialSettings,
  savePortSettings,
  saveSerialSettings
} from './local-printer.settings';

/**
 * Web Serial transport.
 *
 * Owns the lifetime of the serial port the receipt printer is attached to, so
 * nothing else in the app touches navigator.serial.
 *
 * Two ways in:
 *   autoConnect() - reopens a port the user granted before. No gesture
 *                   needed, so it is safe to call on startup.
 *   connect()     - shows the browser's port picker. MUST be called from a
 *                   click handler, or the browser rejects it.
 *
 * Ported from POSUI client/js/shared/serial/webserial.service.js.
 */

export type WebSerialState = {
  supported: boolean;
  connected: boolean;
  portLabel: string;
  lastError: string | null;
};

type Listener = (state: WebSerialState) => void;

let port: SerialPort | null = null;
let writer: WritableStreamDefaultWriter<Uint8Array> | null = null;

let state: WebSerialState = {
  supported: !!getSerial(),
  connected: false,
  portLabel: '',
  lastError: null
};

const listeners = new Set<Listener>();

const setState = (patch: Partial<WebSerialState>) => {
  state = { ...state, ...patch };
  listeners.forEach(listener => listener(state));
};

const hex = (n?: number) =>
  n === undefined || n === null ? '????' : `000${n.toString(16)}`.slice(-4);

const label = (p: SerialPort) => {
  const info = p.getInfo ? p.getInfo() : {};
  if (info.usbVendorId) {
    return `USB ${hex(info.usbVendorId)}:${hex(info.usbProductId)}`;
  }
  return 'Serial port';
};

const describe = (err: any): string => {
  if (!err) return '';
  if (err.name === 'NetworkError') {
    return 'The printer was unplugged or is already in use by another program.';
  }
  if (err.name === 'SecurityError') {
    return 'Serial access was blocked. The page must be served over HTTPS or localhost.';
  }
  if (err.name === 'InvalidStateError') return 'That port is already open.';
  return err.message || String(err);
};

const WRITE_TIMEOUT_MS = 8000;

/**
 * A write that never settles (printer offline, lid open, flow control held)
 * would block every later job in the print queue, so give up and say so.
 */
const withTimeout = <T>(work: Promise<T>, ms: number, message: string) =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    work.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      err => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });

const release = async () => {
  if (writer) {
    const w = writer;
    writer = null;
    try {
      await withTimeout(w.close(), 2000, 'close timed out');
    } catch (e) {
      // Closing a writer on a port that has gone away throws; nothing to do.
    }
    try {
      w.releaseLock();
    } catch (e) {
      // Already released.
    }
  }
  if (port) {
    try {
      await port.close();
    } catch (e) {
      // Already closed.
    }
  }
};

/** Remember this port as the printer, so startup reopens the same one. */
const rememberPort = (p: SerialPort) => {
  const info = p.getInfo ? p.getInfo() : {};
  savePortSettings({
    usbVendorId: info.usbVendorId,
    usbProductId: info.usbProductId,
    autoConnect: true
  });
};

/**
 * The granted port that was chosen last time. Falls back to the only granted
 * port when nothing is remembered yet (a till set up before ports were
 * remembered), but never guesses between several.
 */
const pickRememberedPort = (ports: SerialPort[]) => {
  const saved = getPortSettings();
  if (saved.usbVendorId !== undefined) {
    const match = ports.find(p => {
      const info = p.getInfo ? p.getInfo() : {};
      return (
        info.usbVendorId === saved.usbVendorId &&
        info.usbProductId === saved.usbProductId
      );
    });
    if (match) return match;
  }
  return ports.length === 1 ? ports[0] : undefined;
};

const open = async (selected: SerialPort, baudRate?: number) => {
  await release();
  port = selected;
  const settings = getSerialSettings();
  await port.open({
    baudRate: Number(baudRate || settings.baudRate),
    dataBits: Number(settings.dataBits),
    stopBits: Number(settings.stopBits),
    parity: settings.parity
  });
  if (!port.writable) {
    throw new Error('The printer port opened but cannot be written to.');
  }
  writer = port.writable.getWriter();
  setState({ connected: true, portLabel: label(port), lastError: null });
};

export const webSerial = {
  getState: () => state,

  subscribe: (listener: Listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  setError: (message: string | null) => setState({ lastError: message }),

  isConnected: () => state.connected,

  /**
   * Reopen a previously granted port. Resolves false when there is nothing to
   * reopen, which is the normal state before a printer has been picked.
   */
  autoConnect: async (): Promise<boolean> => {
    const serial = getSerial();
    if (!serial) return false;
    // Disconnected on purpose: stay closed until Connect printer is pressed.
    if (!getPortSettings().autoConnect) return false;
    try {
      const ports = await serial.getPorts();
      const remembered = pickRememberedPort(ports || []);
      if (!remembered) return false;
      await open(remembered);
      rememberPort(remembered);
      return true;
    } catch (err) {
      // eslint-disable-next-line
      console.warn('Local printer: autoConnect failed', err);
      setState({ lastError: describe(err) });
      return false;
    }
  },

  /** Show the port picker. Only works inside a user gesture. */
  connect: async (): Promise<boolean> => {
    const serial = getSerial();
    if (!serial) {
      throw new Error(
        'This browser has no Web Serial support. Use Chrome or Edge on desktop.'
      );
    }
    try {
      const chosen = await serial.requestPort();
      await open(chosen);
      rememberPort(chosen);
      return true;
    } catch (err) {
      // The user closing the picker is not an error worth showing.
      if (err instanceof Error && err.name === 'NotFoundError') return false;
      setState({ lastError: describe(err) });
      throw err;
    }
  },

  /**
   * Close the port and stay closed: a reload or the next sale will not
   * reopen it until Connect printer is pressed again.
   */
  disconnect: async () => {
    await release();
    port = null;
    savePortSettings({ ...getPortSettings(), autoConnect: false });
    setState({ connected: false, portLabel: '' });
  },

  /** Reopen at another line speed without saving it. Used by the baud sweep. */
  reopenAt: async (baudRate: number) => {
    if (!port) throw new Error('No printer connected');
    await open(port, baudRate);
  },

  /** Adopt a line speed permanently and reopen on it. */
  useBaudRate: async (baudRate: number) => {
    saveSerialSettings({ baudRate: Number(baudRate) });
    if (port) await open(port);
  },

  getBaudRate: () => getSerialSettings().baudRate,

  write: async (bytes: Uint8Array) => {
    if (!writer) throw new Error('No printer connected');
    try {
      await withTimeout(
        writer.write(bytes),
        WRITE_TIMEOUT_MS,
        'The printer did not accept the receipt. Check it is switched on, has paper and the lid is closed.'
      );
    } catch (err) {
      setState({ lastError: describe(err) });
      throw err;
    }
  },

  /**
   * Read whatever the printer sends back, giving up after timeoutMs.
   *
   * Used for the real-time status commands (DLE EOT), which answer with one
   * byte. Resolves an empty array on timeout rather than throwing: a printer
   * that does not answer is reported as "unknown", as the connector does.
   */
  read: (timeoutMs = 500, expectedBytes = 1): Promise<number[]> => {
    if (!port || !port.readable) return Promise.resolve([]);

    const reader = port.readable.getReader();
    const collected: number[] = [];

    return new Promise(resolve => {
      let settled = false;

      const finish = async () => {
        if (settled) return;
        settled = true;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        clearTimeout(timer);
        try {
          await reader.cancel();
        } catch (e) {
          // Cancelling a reader on a closed port throws; nothing to do.
        }
        try {
          reader.releaseLock();
        } catch (e) {
          // Already released.
        }
        resolve(collected);
      };

      const timer = setTimeout(finish, timeoutMs);

      const pump = async (): Promise<void> => {
        try {
          while (!settled) {
            // eslint-disable-next-line no-await-in-loop
            const { value, done } = await reader.read();
            if (value) collected.push(...Array.from(value));
            if (done || collected.length >= expectedBytes) break;
          }
        } catch (e) {
          // Treated as "no answer".
        }
        finish();
      };

      pump();
    });
  }
};

// Keep the UI honest when someone pulls the cable out.
const serial = getSerial();
if (serial) {
  serial.addEventListener('disconnect', (event: Event) => {
    if (port && event.target === port) {
      writer = null;
      port = null;
      setState({
        connected: false,
        portLabel: '',
        lastError: 'The printer was disconnected.'
      });
    }
  });
}
